/**
 * DebugLogger - Tagged debug logging framework
 * 
 * Provides structured debug logging with tags for easy filtering and cleanup.
 * All debug logs are tagged and can be enabled/disabled via environment variable.
 */

// Check if debug logging is enabled
// @date_of_change: 2025-09-29 - Extended debug enablement to include NODE_ENV and DEBUG vars
export const DEBUG_ENABLED = typeof process !== "undefined"
  ? !!(process.env.DEBUG_WINDSURF || process.env.DEBUG || (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_LOGS))
  : (typeof window !== "undefined" && (window.DEBUG_WINDSURF === true || window.DEBUG === true));

/**
 * Debug logger with tag taxonomy
 * @param {string} tag - Debug tag (IO, VALIDATION, PARITY, MIGRATION, FORMMAP, COMBAT)
 * @param {...any} args - Arguments to log
 */
export function dlog(tag, ...args) {
  if (DEBUG_ENABLED) {
    console.debug(`[DEBUG:${tag}]`, ...args);
  }
}

/**
 * Specific loggers for different subsystems
 */
export const logIO = (...args) => dlog("IO", ...args);
export const logValidation = (...args) => dlog("VALIDATION", ...args);
export const logParity = (...args) => dlog("PARITY", ...args);
export const logMigration = (...args) => dlog("MIGRATION", ...args);
export const logFormMap = (...args) => dlog("FORMMAP", ...args);
export const logCombat = (...args) => dlog("COMBAT", ...args);
// @date_of_change: 2025-09-29 - Added multiplayer session debug logging
export const logMultiplayer = (...args) => dlog("MULTIPLAYER", ...args);
export const logWebSocket = (...args) => dlog("WEBSOCKET", ...args);

/**
 * Performance timing logger
 * @param {string} tag - Debug tag
 * @param {string} operation - Operation name
 * @param {Function} fn - Function to time
 * @returns {any} - Result of the function
 */
export async function timeOperation(tag, operation, fn) {
  if (!DEBUG_ENABLED) {
    return await fn();
  }
  
  const start = performance.now();
  try {
    const result = await fn();
    const duration = performance.now() - start;
    dlog(tag, `${operation} completed in ${duration.toFixed(2)}ms`);
    return result;
  } catch (error) {
    const duration = performance.now() - start;
    dlog(tag, `${operation} failed after ${duration.toFixed(2)}ms:`, error);
    throw error;
  }
}

/**
 * Object diff logger for parity testing
 * @param {string} tag - Debug tag
 * @param {Object} obj1 - First object
 * @param {Object} obj2 - Second object
 * @param {string} label1 - Label for first object
 * @param {string} label2 - Label for second object
 */
export function logObjectDiff(tag, obj1, obj2, label1 = "Object 1", label2 = "Object 2") {
  if (!DEBUG_ENABLED) return;
  
  const diff = findObjectDifferences(obj1, obj2);
  if (diff.length === 0) {
    dlog(tag, `✅ ${label1} and ${label2} are identical`);
  } else {
    dlog(tag, `❌ ${label1} and ${label2} differ:`, diff);
  }
}

/**
 * Find differences between two objects (for parity checking)
 * @param {Object} obj1 - First object
 * @param {Object} obj2 - Second object
 * @param {string} path - Current path (for recursion)
 * @returns {Array} - Array of difference descriptions
 */
function findObjectDifferences(obj1, obj2, path = "") {
  const differences = [];
  
  // Handle null/undefined cases
  if (obj1 === obj2) return differences;
  if (obj1 == null || obj2 == null) {
    differences.push(`${path}: ${obj1} !== ${obj2}`);
    return differences;
  }
  
  // Handle primitive types
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    if (obj1 !== obj2) {
      differences.push(`${path}: ${obj1} !== ${obj2}`);
    }
    return differences;
  }
  
  // Handle arrays
  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    differences.push(`${path}: type mismatch (array vs object)`);
    return differences;
  }
  
  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) {
      differences.push(`${path}: array length ${obj1.length} !== ${obj2.length}`);
    }
    const maxLength = Math.max(obj1.length, obj2.length);
    for (let i = 0; i < maxLength; i++) {
      const subPath = `${path}[${i}]`;
      differences.push(...findObjectDifferences(obj1[i], obj2[i], subPath));
    }
    return differences;
  }
  
  // Handle objects
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  const allKeys = new Set([...keys1, ...keys2]);
  
  for (const key of allKeys) {
    const subPath = path ? `${path}.${key}` : key;
    
    if (!(key in obj1)) {
      differences.push(`${subPath}: missing in first object`);
    } else if (!(key in obj2)) {
      differences.push(`${subPath}: missing in second object`);
    } else {
      differences.push(...findObjectDifferences(obj1[key], obj2[key], subPath));
    }
  }
  
  return differences;
}

/**
 * Strips transient fields from objects for parity testing
 * @param {Object} obj - Object to strip
 * @returns {Object} - Object with transient fields removed
 */
export function stripTransient(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  const copy = structuredClone(obj);
  
  // Remove known transient fields
  const transientFields = [
    'exportDate',
    'lastModified',
    'createdAt',
    'isLoading',
    'isEditing',
    'isDirty',
    'validationErrors',
    'uiState'
  ];
  
  function removeTransientRecursive(obj) {
    if (!obj || typeof obj !== 'object') return;
    
    for (const field of transientFields) {
      delete obj[field];
    }
    
    for (const value of Object.values(obj)) {
      if (typeof value === 'object') {
        removeTransientRecursive(value);
      }
    }
  }
  
  removeTransientRecursive(copy);
  return copy;
}

const DebugLogger = {
  DEBUG_ENABLED,
  dlog,
  logIO,
  logValidation,
  logParity,
  logMigration,
  logFormMap,
  logCombat,
  logMultiplayer,
  logWebSocket,
  timeOperation,
  logObjectDiff,
  stripTransient
};

export default DebugLogger;
